import React, { useState, useEffect } from 'react';
import {
  Settings,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  DollarSign,
  Clock,
  Calendar,
  Save,
  AlertTriangle,
  CheckSquare,
  Users
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { FirmAdminLayout } from './FirmAdminLayout';
import HolidayManagement from './HolidayManagement';
import LeaveTypesManagement from './LeaveTypesManagement';
import LeaveApprovalSettings from './LeaveApprovalSettings';
import toast from 'react-hot-toast';

interface FirmSettings {
  name: string;
  address: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  contact: {
    phone: string;
    email: string;
    website: string;
  };
  billing: {
    defaultHourlyRate: number;
    currency: 'CAD' | 'USD';
    taxNumber: string;
    paymentTerms: number;
  };
  preferences: {
    timeZone: string;
    dateFormat: 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';
    workingHours: {
      start: string;
      end: string;
      workingDays: number[];
    };
  };
}

const FormSection: React.FC<{
  title: string;
  description: string;
  icon: React.ElementType;
  children: React.ReactNode;
}> = ({ title, description, icon: Icon, children }) => (
  <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
    <div className="flex items-center mb-4">
      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
        <Icon className="w-5 h-5 text-primary-600" />
      </div>
      <div>
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
    {children}
  </div>
);

const InputField: React.FC<{
  label: string;
  name: string;
  type?: string;
  value: string | number;
  onChange: (name: string, value: string | number) => void;
  placeholder?: string;
  required?: boolean;
}> = ({ label, name, type = 'text', value, onChange, placeholder, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={(e) => onChange(name, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      required={required}
    />
  </div>
);

const SelectField: React.FC<{
  label: string;
  name: string;
  value: string;
  onChange: (name: string, value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
}> = ({ label, name, value, onChange, options, required }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      required={required}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

export const FirmSettings: React.FC = () => {
  const { user, tenant } = useAuthStore();
  const [settings, setSettings] = useState<FirmSettings>({
    name: '',
    address: {
      street: '',
      city: '',
      province: '',
      postalCode: '',
      country: 'Canada',
    },
    contact: {
      phone: '',
      email: '',
      website: '',
    },
    billing: {
      defaultHourlyRate: 150,
      currency: 'CAD',
      taxNumber: '',
      paymentTerms: 30,
    },
    preferences: {
      timeZone: 'America/Toronto',
      dateFormat: 'MM/DD/YYYY',
      workingHours: {
        start: '09:00',
        end: '17:00',
        workingDays: [1, 2, 3, 4, 5],
      },
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/firm-admin/settings');
      
      if (response.data.success) {
        setSettings(response.data.data);
      } else {
        throw new Error('Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (section: keyof FirmSettings, field: string, value: string | number) => {
    if (section === 'name') {
      // Handle direct name field
      setSettings(prev => ({
        ...prev,
        name: value as string,
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    }
  };

  const handleNestedInputChange = (section: keyof FirmSettings, field: string, value: string | number) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleWorkingHoursChange = (field: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        workingHours: {
          ...prev.preferences.workingHours,
          [field]: value,
        },
      },
    }));
  };

  const handleWorkingDaysChange = (day: number, checked: boolean) => {
    setSettings(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        workingHours: {
          ...prev.preferences.workingHours,
          workingDays: checked
            ? [...prev.preferences.workingHours.workingDays, day].sort()
            : prev.preferences.workingHours.workingDays.filter(d => d !== day),
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.patch('/firm-admin/settings', settings);
      
      if (response.data.success) {
        toast.success('Settings saved successfully');
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <FirmAdminLayout>
        <div className="p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading settings...</p>
          </div>
        </div>
      </FirmAdminLayout>
    );
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const provinces = [
    { value: 'AB', label: 'Alberta' },
    { value: 'BC', label: 'British Columbia' },
    { value: 'MB', label: 'Manitoba' },
    { value: 'NB', label: 'New Brunswick' },
    { value: 'NL', label: 'Newfoundland and Labrador' },
    { value: 'NS', label: 'Nova Scotia' },
    { value: 'ON', label: 'Ontario' },
    { value: 'PE', label: 'Prince Edward Island' },
    { value: 'QC', label: 'Quebec' },
    { value: 'SK', label: 'Saskatchewan' },
    { value: 'NT', label: 'Northwest Territories' },
    { value: 'NU', label: 'Nunavut' },
    { value: 'YT', label: 'Yukon' },
  ];

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Firm Settings</h1>
              <p className="text-gray-600">Configure your firm's information and preferences</p>
            </div>

            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          {/* Firm Information */}
          <FormSection
            title="Firm Information"
            description="Basic information about your accounting firm"
            icon={Building2}
          >
            <div className="grid grid-cols-1 gap-6">
              <InputField
                label="Firm Name"
                name="name"
                value={settings.name}
                onChange={(name, value) => handleInputChange('name' as any, '', value)}
                placeholder="Enter your firm name"
                required
              />
            </div>
          </FormSection>

          {/* Address Information */}
          <FormSection
            title="Address Information"
            description="Your firm's physical address"
            icon={MapPin}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <InputField
                  label="Street Address"
                  name="street"
                  value={settings.address.street}
                  onChange={(name, value) => handleNestedInputChange('address', name, value)}
                  placeholder="123 Main Street"
                />
              </div>
              <InputField
                label="City"
                name="city"
                value={settings.address.city}
                onChange={(name, value) => handleNestedInputChange('address', name, value)}
                placeholder="Toronto"
              />
              <SelectField
                label="Province"
                name="province"
                value={settings.address.province}
                onChange={(name, value) => handleNestedInputChange('address', name, value)}
                options={provinces}
              />
              <InputField
                label="Postal Code"
                name="postalCode"
                value={settings.address.postalCode}
                onChange={(name, value) => handleNestedInputChange('address', name, value)}
                placeholder="M5V 3A8"
              />
              <InputField
                label="Country"
                name="country"
                value={settings.address.country}
                onChange={(name, value) => handleNestedInputChange('address', name, value)}
                placeholder="Canada"
              />
            </div>
          </FormSection>

          {/* Contact Information */}
          <FormSection
            title="Contact Information"
            description="How clients can reach your firm"
            icon={Phone}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Phone Number"
                name="phone"
                value={settings.contact.phone}
                onChange={(name, value) => handleNestedInputChange('contact', name, value)}
                placeholder="(416) 555-0123"
              />
              <InputField
                label="Email Address"
                name="email"
                type="email"
                value={settings.contact.email}
                onChange={(name, value) => handleNestedInputChange('contact', name, value)}
                placeholder="info@yourfirm.com"
              />
              <div className="md:col-span-2">
                <InputField
                  label="Website"
                  name="website"
                  type="url"
                  value={settings.contact.website}
                  onChange={(name, value) => handleNestedInputChange('contact', name, value)}
                  placeholder="https://www.yourfirm.com"
                />
              </div>
            </div>
          </FormSection>

          {/* Billing Settings */}
          <FormSection
            title="Billing Settings"
            description="Default billing rates and preferences"
            icon={DollarSign}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputField
                label="Default Hourly Rate"
                name="defaultHourlyRate"
                type="number"
                value={settings.billing.defaultHourlyRate}
                onChange={(name, value) => handleNestedInputChange('billing', name, value)}
                placeholder="150"
              />
              <SelectField
                label="Currency"
                name="currency"
                value={settings.billing.currency}
                onChange={(name, value) => handleNestedInputChange('billing', name, value)}
                options={[
                  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
                  { value: 'USD', label: 'US Dollar (USD)' },
                ]}
              />
              <InputField
                label="Tax Number"
                name="taxNumber"
                value={settings.billing.taxNumber}
                onChange={(name, value) => handleNestedInputChange('billing', name, value)}
                placeholder="123456789RT0001"
              />
              <InputField
                label="Payment Terms (Days)"
                name="paymentTerms"
                type="number"
                value={settings.billing.paymentTerms}
                onChange={(name, value) => handleNestedInputChange('billing', name, value)}
                placeholder="30"
              />
            </div>
          </FormSection>

          {/* Preferences */}
          <FormSection
            title="Preferences"
            description="System preferences and working hours"
            icon={Clock}
          >
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SelectField
                  label="Time Zone"
                  name="timeZone"
                  value={settings.preferences.timeZone}
                  onChange={(name, value) => handleNestedInputChange('preferences', name, value)}
                  options={[
                    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
                    { value: 'America/Winnipeg', label: 'Central Time (Winnipeg)' },
                    { value: 'America/Edmonton', label: 'Mountain Time (Edmonton)' },
                    { value: 'America/Vancouver', label: 'Pacific Time (Vancouver)' },
                  ]}
                />
                <SelectField
                  label="Date Format"
                  name="dateFormat"
                  value={settings.preferences.dateFormat}
                  onChange={(name, value) => handleNestedInputChange('preferences', name, value)}
                  options={[
                    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
                  ]}
                />
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Working Hours</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <InputField
                    label="Start Time"
                    name="start"
                    type="time"
                    value={settings.preferences.workingHours.start}
                    onChange={(name, value) => handleWorkingHoursChange(name, value as string)}
                  />
                  <InputField
                    label="End Time"
                    name="end"
                    type="time"
                    value={settings.preferences.workingHours.end}
                    onChange={(name, value) => handleWorkingHoursChange(name, value as string)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Working Days</label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {dayNames.map((day, index) => (
                      <label key={index} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.preferences.workingHours.workingDays.includes(index)}
                          onChange={(e) => handleWorkingDaysChange(index, e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-2"
                        />
                        <span className="text-sm text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </FormSection>

          {/* Holiday Management */}
          <FormSection
            title="Holiday Management"
            description="Manage company holidays that will be displayed in attendance calendars"
            icon={Calendar}
          >
            <HolidayManagement />
          </FormSection>

          {/* Leave Types Management */}
          <FormSection
            title="Leave Types Management"
            description="Configure leave types available for employees to apply for"
            icon={Settings}
          >
            <LeaveTypesManagement />
          </FormSection>

          {/* Leave Approval Settings */}
          <FormSection
            title="Leave Approval Settings"
            description="Configure how leave applications are approved in your organization"
            icon={CheckSquare}
          >
            <LeaveApprovalSettings />
          </FormSection>
        </div>
      </div>
    </FirmAdminLayout>
  );
};
